// joyrideProcess.js
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openSubMenu, closeSubMenu } from "./../../contents/redux/actions/subMenuActions"; // Adjust path
import { nextStep } from "./../../contents/redux/actions/userGuideActions"; // Adjust path

export const waitForElement = (selector, options = {}) => {
  const { timeout = 5000, checkVisibility = true, interval = 100 } = options;
  const startTime = Date.now();

  return new Promise((resolve) => {
    const checkElement = () => {
      const element = document.querySelector(selector);
      if (element) {
        const isVisible = !checkVisibility || (
          element.offsetWidth > 0 &&
          element.offsetHeight > 0 &&
          window.getComputedStyle(element).display !== "none" &&
          window.getComputedStyle(element).visibility !== "hidden"
        );
        if (isVisible) {
          console.log(`Element found: ${selector}`);
          resolve(element);
          return;
        }
      }
      if (Date.now() - startTime >= timeout) {
        console.warn(`Timeout waiting for element: ${selector}`);
        resolve(null);
      } else {
        setTimeout(checkElement, interval);
      }
    };
    checkElement();
  });
};

export const useHandleDropdownInteraction = () => {
  const dispatch = useDispatch();
  const openSubMenuId = useSelector((state) => state.subMenu.openSubMenuId);

  return useCallback(
    async (step) => {
      const { dropdownId, openDelay = 800, maxAttempts = 3 } = step;
      if (!dropdownId) return false;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        console.log(`Dropdown attempt ${attempt}/${maxAttempts} for ${dropdownId}`);
        if (openSubMenuId !== dropdownId) {
          dispatch(openSubMenu(dropdownId));
          await new Promise((resolve) => setTimeout(resolve, openDelay));
        }
        const dropdown = await waitForElement(`[data-submenu-id="${dropdownId}"]`, {
          timeout: 1000,
          checkVisibility: true,
        });
        if (dropdown) return true;
        dispatch(closeSubMenu());
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      return false;
    },
    [dispatch, openSubMenuId]
  );
};

export const usePrepareStep = (navigate, location) => {
  const dispatch = useDispatch();
  const handleDropdownInteraction = useHandleDropdownInteraction();

  return useCallback(
    async (step) => {
      if (!step) return false;
      dispatch({ type: "SET_TOUR_STATE", payload: { isNavigating: false, isWaitingForElement: true, isStepReady: false } });
      try {
        if (step.route && step.route !== location.pathname) {
          dispatch({ type: "SET_TOUR_STATE", payload: { isNavigating: true } });
          navigate(step.route);
          await new Promise((resolve) => setTimeout(resolve, step.waitAfterNavigation || 1000));
        }
        if (step.requiresDropdown && !await handleDropdownInteraction(step)) {
          console.warn("Dropdown interaction failed");
          return false;
        }
        if (step.openModal) {
          dispatch({ type: "OPEN_MODAL" }); // Trigger modal opening
          await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for modal to render
        }
        if (step.waitForComponent) {
          const component = await waitForElement(step.waitForComponent, {
            timeout: step.targetTimeout || 15000,
            checkVisibility: false,
          });
          if (!component) {
            console.warn(`Component not ready: ${step.waitForComponent}`);
            return false;
          }
        }
        const target = await (async () => {
          for (let attempt = 1; attempt <= 10; attempt++) {
            const element = await waitForElement(step.target, {
              timeout: 3000,
              checkVisibility: step.checkTargetVisibility !== false,
            });
            if (element) return element;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
          return null;
        })();

        if (!target) {
          console.warn(`Target not found: ${step.target}`);
          if (step.fallbackSelectors) {
            for (const fallback of step.fallbackSelectors) {
              const fallbackElement = await waitForElement(fallback);
              if (fallbackElement) return true;
            }
          }
          return false;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Ensure Joyride has time to render
        dispatch({ type: "SET_TOUR_STATE", payload: { isStepReady: true, lastStepPrepared: Date.now() } });
        return true;
      } catch (error) {
        console.error("Step preparation error:", error);
        return false;
      } finally {
        dispatch({ type: "SET_TOUR_STATE", payload: { isNavigating: false, isWaitingForElement: false } });
      }
    },
    [navigate, location.pathname, handleDropdownInteraction, dispatch]
  );
};

export const useJoyrideStepPreparation = (navigate, location) => {
  const dispatch = useDispatch();
  const { isRunning, currentStep, currentSteps } = useSelector((state) => state.userGuide);
  const prepareStep = usePrepareStep(navigate, location);

  useEffect(() => {
    if (!isRunning || currentSteps.length === 0 || currentStep >= currentSteps.length) {
      if (currentStep >= currentSteps.length) {
        console.log("Tour completed or no more steps available");
        dispatch({ type: "STOP_TOUR" });
      }
      return;
    }
    const controller = new AbortController();
    const prepareCurrentStep = async () => {
      const success = await prepareStep(currentSteps[currentStep]);
      if (!controller.signal.aborted && !success && currentStep < currentSteps.length - 1) {
        dispatch(nextStep());
      }
    };
    prepareCurrentStep();
    return () => controller.abort();
  }, [isRunning, currentStep, currentSteps, prepareStep, dispatch]);
};
